import clsx from "clsx";
import { motion } from "framer-motion";
import { GatsbyImage } from "gatsby-plugin-image";
import React, { FC, useCallback, useRef, useState } from "react";

import { useBreakpoints } from "../../../hooks/use-breakpoints";
import { Icon } from "../../icon";
import { PaginationBar } from "../../pagination/pagination-bar";

export interface ProductCardGalleryProps {
  product: Pick<Queries.ProductDataFragment, "cardImages" | "name">;
}

const aspectRatio = "aspect-[16/9]";

const commonBtnArrowClasses = [
  "from-transparent to-[rgba(0,0,0,0.75)] hidden lg:block w-1/3 text-light",
  "absolute top-0 h-full z-20 opacity-0 hover:opacity-100 px-2",
];

export const ProductCardGallery: FC<ProductCardGalleryProps> = ({ product }) => {
  const { name, cardImages } = product;
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const imagesCount = cardImages?.length ?? 0;

  const constraintsRef = useRef(null);

  const changePage = useCallback(
    (delta: 1 | -1) => {
      setSelectedImageIndex((index) => (index + imagesCount + delta) % imagesCount);
    },
    [imagesCount]
  );

  const { lg } = useBreakpoints();

  return (
    <div className={clsx("w-full col-span-1", imagesCount > 1 && "cursor-pointer")}>
      <div className={clsx("overflow-x-hidden relative", aspectRatio)} ref={constraintsRef}>
        <motion.div
          className={clsx(
            "flex items-center h-full rounded-t-2xl overflow-hidden",
            "lg:transition-all lg:ease-out lg:duration-300"
          )}
          animate={{
            x: `${(-100 * selectedImageIndex) / imagesCount}%`,
          }}
          style={{
            width: `${imagesCount * 100}%`,
          }}
          {...(!lg && {
            drag: "x",
            dragElastic: 0,
            onDragEnd: (_, { offset }) => {
              if (offset.x < 0) {
                changePage(1);
              } else if (offset.x > 0) {
                changePage(-1);
              }
            },
            dragMomentum: false,
          })}
        >
          {cardImages?.map((image, i) => (
            <GatsbyImage
              image={image?.gatsbyImageData!}
              alt={name ?? ""}
              key={i}
              objectFit="cover"
              className={clsx("h-full pointer-events-none flex-none", aspectRatio)}
            />
          ))}
        </motion.div>
        {/* Arrows */}
        {imagesCount > 1 && (
          <>
            <button
              type="button"
              onClick={() => changePage(-1)}
              className={clsx("bg-gradient-to-l left-0", commonBtnArrowClasses)}
            >
              <Icon icon="arrow-left" className="h-16" />
            </button>
            <button
              type="button"
              onClick={() => changePage(1)}
              className={clsx("bg-gradient-to-r right-0", commonBtnArrowClasses)}
            >
              <Icon icon="arrow-right" className="h-16 ml-auto" />
            </button>
          </>
        )}
      </div>

      <PaginationBar
        activeItem={selectedImageIndex}
        totalItems={cardImages?.length ?? 1}
        className={clsx("absolute bottom-0 bg-light w-full", !cardImages?.length && "invisible")}
      />
    </div>
  );
};
